import { Connection, PublicKey } from '@solana/web3.js'
import { Metaplex } from '@metaplex-foundation/js'

/**
 * Pythenians NFT Collection Details
 * Collection Address: pyTh2UtBKfuDW6KCdT3swospYeoLmmKaGujWA91Moru (verified collection mint)
 * Old/Incorrect: 60f7f69070dfc98235f75832de1c5aee571a5b34c266169c21f47763ffb742ca
 */
const PYTHENIANS_COLLECTION_ADDRESS = 'pyTh2UtBKfuDW6KCdT3swospYeoLmmKaGujWA91Moru'

/**
 * Verify if a wallet owns a Pythenians NFT
 * @param {string} walletAddress - The Solana wallet address to check
 * @param {string} rpcUrl - Solana RPC endpoint URL
 * @returns {Promise<{hasNFT: boolean, nft: object|null, nfts: array}>}
 */
export async function verifyPytheniansNFT(walletAddress, rpcUrl = null) {
  try {
    // Use provided RPC or fallback to environment variable or public endpoint
    const endpoint = rpcUrl ||
                    import.meta.env.VITE_SOLANA_RPC_URL ||
                    'https://api.mainnet-beta.solana.com'

    // Create connection to Solana
    const connection = new Connection(endpoint, 'confirmed')

    // Create Metaplex instance
    const metaplex = Metaplex.make(connection)

    // Convert wallet address to PublicKey
    const ownerPublicKey = new PublicKey(walletAddress)

    // Find all NFTs owned by this wallet
    const nfts = await metaplex
      .nfts()
      .findAllByOwner({ owner: ownerPublicKey })

    // Filter for Pythenians NFTs
    // Check if any NFT belongs to the Pythenians collection
    const pytheniansNFTs = nfts.filter(nft => {
      // Check collection
      if (nft.collection) {
        const collectionAddress = nft.collection.address.toString()
        return collectionAddress === PYTHENIANS_COLLECTION_ADDRESS
      }
      return false
    })

    if (pytheniansNFTs.length > 0) {
      // Load full metadata for the first Pythenians NFT
      const firstNFT = pytheniansNFTs[0]

      const nftWithMetadata = await metaplex
        .nfts()
        .load({ metadata: firstNFT })

      // Try to get image from various possible locations
      let imageUrl = null
      let description = null
      let attributes = []

      if (nftWithMetadata.json?.image) {
        // Metadata already loaded
        imageUrl = nftWithMetadata.json.image
        description = nftWithMetadata.json.description
        attributes = nftWithMetadata.json.attributes || []
      } else if (nftWithMetadata.uri) {
        // Fetch metadata from URI (IPFS/Arweave)
        try {
          const response = await fetch(nftWithMetadata.uri)
          const jsonMetadata = await response.json()
          imageUrl = jsonMetadata.image
          description = jsonMetadata.description
          attributes = jsonMetadata.attributes || []
        } catch (error) {
          // Failed to fetch metadata from URI
        }
      }

      const nftData = {
        name: nftWithMetadata.name,
        symbol: nftWithMetadata.symbol,
        image: imageUrl,
        description: description,
        attributes: attributes,
        mintAddress: nftWithMetadata.mint.address.toString(),
        collection: PYTHENIANS_COLLECTION_ADDRESS
      }

      return {
        hasNFT: true,
        nft: nftData,
        nfts: pytheniansNFTs.map(n => ({
          name: n.name,
          mint: n.mint?.address?.toString() || n.address?.toString() || 'unknown'
        }))
      }
    }

    return {
      hasNFT: false,
      nft: null,
      nfts: []
    }

  } catch (error) {
    // Return error state
    return {
      hasNFT: false,
      nft: null,
      nfts: [],
      error: error.message
    }
  }
}

/**
 * Get Pythenians collection info
 */
export function getPytheniansCollectionInfo() {
  return {
    name: 'Pythenians',
    address: PYTHENIANS_COLLECTION_ADDRESS,
    marketplaces: {
      magicEden: 'https://magiceden.io/marketplace/pythenians',
      tensor: 'https://www.tensor.trade/trade/pythenians',
      coingecko: 'https://www.coingecko.com/en/nft/pythenians'
    }
  }
}
